import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { PageLoader } from "./components/page-loader";
import TableauEmbed from "./components/Tableau/TableauEmbed";
import {
  Login,
  TopHeader,
  SecondHeader,
  Dashboard,
  DashboardDetails,
  SideNav,
  TableauView,
  UserManagement,
  UserDetails,
  AddUser,
  RoleBaseRoute,
  AccountCreateConfirmation,
  SiteBanner,
} from "./components/tsx-components/index.tsx";
import "bootstrap/dist/css/bootstrap.min.css";
import AutoLogout from "./components/tsx-components/AutoLogout/AutoLogout.tsx";
import CopyrightModal from "./components/copyright-modal.js";
import { useAppDispatch, useTypedSelector, clearAppState } from "./store/app.store.tsx";
import { FetchAppRoles } from "./store/roles.thunk.tsx";
import { fetchDashboardAccess, clearAuthenticationState } from "./store/authentication.reducer.tsx";
import { clearAppRolesState } from "./store/roles.reducer.tsx";
import { clearUserState } from "./store/user.reducer.tsx";
import { clearDashboardState } from "./store/dashboard.reducer.tsx";
import { clearGroupState } from "./store/groups.reducer.tsx";
import { clearFormState } from "./store/forms.reducer.tsx";
import { clearModalState } from "./store/modal.reducer.tsx";
import { parseUserRolesResponse } from "./utils/auth.ts";

const isE2EAuthBypassEnabled = () => {
  if (process.env.REACT_APP_E2E_BYPASS_AUTH === "true") {
    return true;
  }

  if (process.env.NODE_ENV === "production") {
    return false;
  }

  if (typeof window !== "undefined") {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("e2e_auth_bypass") === "true") {
      return true;
    }
  }

  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }

  try {
    return window.localStorage.getItem("e2e_auth_bypass") === "true";
  } catch {
    return false;
  }
};

export const App = () => {
  const auth0 = useAuth0();
  const isE2EAuthBypass = isE2EAuthBypassEnabled();
  const {
    logout,
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    user,
  } = isE2EAuthBypass
    ? {
        logout: () => undefined,
        isLoading: false,
        isAuthenticated: false,
        getAccessTokenSilently: async () => null,
        loginWithRedirect: async () => undefined,
        user: null,
      }
    : auth0;
  const [token, setToken] = useState(null);
  const dispatch = useAppDispatch();
  const [openPolicy, setOpenPolicy] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [sideNavCollapsed, setSideNavCollapsed] = useState(() => {
    const saved = localStorage.getItem('sideNavCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [tableauUrl, setTableauUrl] = useState(null);
  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
  const showAccountCreateConfirmation = useTypedSelector(
    (state) => state.modals.showAccountCreateConfirmation
  );
  const hasDashboardAccess = useTypedSelector(
    (state) => state.authentication.dashboardAccess > 0
  );
  const loadingDashboardAccess = useTypedSelector(
    (state) => state.authentication.loadingDashboardAccess
  );

  const [isDemoMode, setIsDemoMode] = useState(false);

  const tableauOptions = {
    width: "100%",
    height: "calc(100vh - 160px)",
    device: "desktop",
    toolbar: "hidden",
  };
  const adminRoles = ["Administrator", "Site Administrator"];

  const handleLogout = () => {
    dispatch(clearAppState());
    dispatch(clearAuthenticationState());
    dispatch(clearAppRolesState());
    dispatch(clearUserState());
    dispatch(clearDashboardState());
    dispatch(clearGroupState());
    dispatch(clearFormState());
    dispatch(clearModalState())
    setToken(null);
    setUserRoles([]);
    logout({
      logoutParams: {
        returnTo: process.env.REACT_APP_URL_REDIRECT,
      },
    });
  };

  const menus = [
    {
      label: "Site Administration",
      key: "siteAdministration",
      roles: adminRoles,
      url: "/users",
    },
    {
      label: "Sign Out",
      key: "signOut",
      click: handleLogout,
    },
  ];

  const authLogin = () => {
    loginWithRedirect()
      .then((authResult) => {
        if (authResult && authResult.accessToken) {
          // Store token
          setToken(authResult.accessToken);
          dispatch(fetchDashboardAccess({ token: authResult.accessToken }));
        }
      })
      .catch((error) => {
        console.error("Authentication error:", error);
      });
  };

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (isAuthenticated) {
        const AUTH0_NAMESPACE = "https://api.app.pfsportal.org";
        const isActive = user?.[`${AUTH0_NAMESPACE}/active`];
        if (isActive === false) {
          localStorage.setItem("pfs_inactive_error", "1");
          logout({ logoutParams: { returnTo: process.env.REACT_APP_URL_REDIRECT } });
          return;
        }
        try {
          const currentToken = await getAccessTokenSilently();
          setToken(currentToken);
          dispatch(fetchDashboardAccess({ token: currentToken }));
          dispatch(FetchAppRoles({ token: currentToken }));
        } catch (error) {
          console.log("Error fetching access token", error);
        }
      }
    };

    const fetchUserRoles = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_PFS_BASE_URL}/auth/${user.email}/roles`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_API_KEY,
            },
          }
        );
        if (!response.ok) {
          setUserRoles([]);
        } else {
          const data = await response.json();
          const { isInactive, roles } = parseUserRolesResponse(data);
          if (isInactive) {
            localStorage.setItem("pfs_inactive_error", "1");
            setToken(null);
            setUserRoles([]);
            setTableauUrl(null);
            logout({ logoutParams: { returnTo: process.env.REACT_APP_URL_REDIRECT } });
            return;
          }
          setUserRoles(roles);
          const demoRole = roles.map(role => role.toLowerCase()).includes('site demo dashboards');
          setIsDemoMode(demoRole);
          console.log('setting tableUrl', demoRole, demoRole ? process.env.REACT_APP_DEMO_TABLEAU_URL : process.env.REACT_APP_TABLEAU_URL)
          setTableauUrl(demoRole ? process.env.REACT_APP_DEMO_TABLEAU_URL : process.env.REACT_APP_TABLEAU_URL)
        }
      } catch (error) {
        console.log("Failed to fetch user roles ", error);
        setUserRoles([]);
      }
    };

    fetchAccessToken();

    // if user is set then pull the roles
    if (user && user.email) {
      fetchUserRoles();
    }
  }, [isAuthenticated, getAccessTokenSilently, isLoading, user, dispatch, logout]);

  const username = user?.email;
  if ((isLoading && !token) || (token && loadingDashboardAccess !== "DONE")) {
    return (
      <div className="page-layout">
        <SiteBanner />
        <PageLoader />
      </div>
    );
  }

  if (isAuthenticated && (loadingDashboardAccess === "LOADING" || !tableauUrl)) {
    return (
      <div className="page-layout">
        <SiteBanner />
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="full-page">
      {/* Sits outside the authenticated branch so downtime notices also reach
          the login screen. */}
      <SiteBanner />
      {showAccountCreateConfirmation && <AccountCreateConfirmation />}
      {!isAuthenticated && <Login onAuthLogin={authLogin} />}
      {isAuthenticated && token && tableauUrl && (
        <div className="app-container">
          <div className="top-banner">
            <AutoLogout
              timeout={1000 * 60 * process.env.REACT_APP_AUTO_LOGOUT_TIME}
            />
          </div>
          <TopHeader user={user} />
          <SecondHeader 
            email={user.email} 
            token={token} 
            isDemoMode={isDemoMode}
            onToggleSideNav={() => {
              const newState = !sideNavCollapsed;
              setSideNavCollapsed(newState);
              localStorage.setItem('sideNavCollapsed', JSON.stringify(newState));
            }}
          />
          <div className={`main-wrapper ${sideNavCollapsed ? 'collapsed' : ''}`}>
            <nav className={`side-nav`}>
              <SideNav user={user} menus={menus} userRoles={userRoles} />
            </nav>
            <div className="main-content">
              <Routes>
                <Route
                  path="/dashboard"
                  element={<Dashboard email={user.email} token={token} />}
                />
                <Route
                  path="/dashboard/:topicType"
                  element={
                    <DashboardDetails email={user.email} token={token} />
                  }
                />
                <Route
                  path="/dashboard/:topicType/:dashboardName"
                  element={
                    <TableauView
                      email={user.email}
                      apiEndpoint={apiEndpoint}
                      token={token}
                    />
                  }
                />
                <Route
                  element={
                    <RoleBaseRoute
                      userRoles={userRoles}
                      allowedRoles={adminRoles}
                    />
                  }
                >
                  <Route
                    path="/users"
                    element={<UserManagement token={token} />}
                  />
                </Route>
                <Route
                  element={
                    <RoleBaseRoute
                      userRoles={userRoles}
                      allowedRoles={adminRoles}
                    />
                  }
                >
                  <Route
                    exact
                    path="/users/:userId/edit"
                    element={<AddUser token={token} />}
                  />
                </Route>
                <Route
                  element={
                    <RoleBaseRoute
                      userRoles={userRoles}
                      allowedRoles={adminRoles}
                    />
                  }
                >
                  <Route
                    exact
                    path="/users/new"
                    element={<AddUser token={token} />}
                  />
                </Route>
                <Route
                  element={
                    <RoleBaseRoute
                      userRoles={userRoles}
                      allowedRoles={adminRoles}
                    />
                  }
                >
                  <Route
                    exact
                    path="/users/:userId"
                    element={<UserDetails token={token} />}
                  />
                </Route>
                <Route path="/home" element={<Navigate to="/" />} />
                <Route
                  path="*"
                  element={ hasDashboardAccess ?
                    <TableauEmbed
                      url={tableauUrl}
                      username={username}
                      options={tableauOptions}
                      token={token}
                    /> : <Navigate to="/dashboard" />
                  }
                />
              </Routes>
            </div>
          </div>
        </div>
      )}
      <div className="copyright">
        <p className="col-center">
          <span>
            Copyright &copy; {new Date().getFullYear()} CSSI -{" "}
            <a href="https://cssiinc.com" target="_blank" rel="noreferrer">
              cssiinc.com
            </a>
          </span>
          <span className="cp-link" onClick={() => setOpenPolicy(true)} href="">
            Privacy Policy
          </span>
        </p>
        <p className="warning">Data contained within the Safety Data Portal is considered Sensitive Unclassified Information (SUI) and must be protected from uncontrolled release to individuals outside the FAA and from indiscriminate sharing within the FAA.</p>
        <CopyrightModal
          isOpen={openPolicy}
          onRequestClose={() => setOpenPolicy(false)}
        />
      </div>
    </div>
  );
};
